'use client';

import { Alert } from '@/components/alert/Alert';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import { convertToHeadlineTag } from '@/components/customFields/utils';
import FaqAccordion from '@/components/faqAccordion/FaqAccordion';
import FaqPageContent from '@/components/faqPageContent';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { generateFaqHeadline, generateFaqOverviewHeadline } from '@/helper/faqUtils';
import type { ComponentHelpcenterContentStoryblok, PageFaqStoryblok } from '@/types/component-types-sb';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './HelpCenterContent.module.scss';
import { type HelpCenterBlockData, fetchHelpCenterBlockData } from './actions';

type FetchStatus = 'idle' | 'loading' | 'error' | 'success';

type HelpCenterContentProps = {
  blok: ComponentHelpcenterContentStoryblok;
};

const HelpCenterContent: FC<HelpCenterContentProps> = ({ blok }) => {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT('de');
  const slug = blok.helpcenterItem?.cached_url;
  const [status, setStatus] = useState<FetchStatus>(() => (slug ? 'loading' : 'idle'));
  const [data, setData] = useState<HelpCenterBlockData | null>(null);

  useEffect(() => {
    if (!slug) {
      setStatus('idle');
      setData(null);
      return;
    }

    let active = true;
    setStatus('loading');
    setData(null);

    void (async () => {
      try {
        const res = await fetchHelpCenterBlockData(slug);
        if (!active) return;
        setData(res);
        setStatus('success');
      } catch (error) {
        console.error('Error fetching HelpCenter data:', error);
        if (!active) return;
        setStatus('error');
      }
    })();

    return () => {
      active = false;
    };
  }, [slug]);

  if (!slug) return null;
  if (status === 'success' && !data) return null;

  return (
    <div className={classes.helpCenterContent}>
      <HeadlineField
        tag={convertToHeadlineTag(blok.headlineStyle, HeadlineTag.H2)}
        style={convertToHeadlineTag(blok.headlineStyle, HeadlineTag.H2)}
      >
        {blok.headline}
      </HeadlineField>

      {status === 'loading' && (
        <div className={classes.loadingHost}>
          <LoadingBackdrop loading type="area" text={t('helpCenter.contentEmbedLoading')} />
        </div>
      )}

      {status === 'error' && (
        <Alert severity="error" role="alert">
          {t('helpCenter.contentEmbedError')}
        </Alert>
      )}

      {status === 'success' && data && data.component === 'page_faq' && (
        <FaqPageContent
          storyId={data.linkedStory.id}
          storySlug={data.linkedStory.full_slug}
          blok={data.linkedStory.content as PageFaqStoryblok}
          helpCenterSettings={data.settings}
          faqHeadline={generateFaqHeadline(t, data.linkedStory.full_slug, data.categories)}
          faqItems={data.faqItems}
          withAccordion={!!blok.withAccordion}
        />
      )}

      {status === 'success' && data && data.component === 'page_faq_overview' && (
        <FaqAccordion
          storyId={data.linkedStory.id}
          headline={generateFaqOverviewHeadline(
            t,
            typeof data.linkedStory.content?.title === 'string' ? data.linkedStory.content.title : undefined
          )}
          faqFeedbackText={data.settings?.faqFeedbackText}
          faqItems={data.faqItems}
        />
      )}
    </div>
  );
};

export default HelpCenterContent;
